import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Colors } from "../../utils/Colors";

const ADMISSION_API = "http://127.0.0.1:8000/api/patients/admissions/";
const INVESTIGATION_API = "http://127.0.0.1:8000/api/investigations/";
const INVESTIGATION_CHOICES_API = "http://127.0.0.1:8000/api/investigations/choices/";

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: `1px solid ${Colors.border.input}`,
  outline: "none",
  fontSize: "14px",
  background: Colors.bg.paper,
  boxSizing: "border-box",
  color: Colors.text.body,
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "110px",
  resize: "vertical",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: 700,
  color: Colors.text.title,
  fontSize: "14px",
};

const readOnlyStyle = {
  ...inputStyle,
  background: Colors.bg.hover,
  color: Colors.text.muted,
};

const sectionCardStyle = {
  background: Colors.bg.paper,
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 4px 14px rgba(11,60,63,0.05)",
};

export default function InvestigationForm() {
  const [admissions, setAdmissions] = useState([]);
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [abnormalFlagOptions, setAbnormalFlagOptions] = useState([]);
  const [loadingChoices, setLoadingChoices] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [form, setForm] = useState({
    admission: "",
    admission_number: "",
    patient_uhid: "",
    patient_name: "",
    doctor_name: "",
    category: "",
    test_name: "",
    body_part_sample: "",
    result: "",
    impression_diagnosis: "",
    status: "",
    date: "",
    remarks: "",
    unit: "",
    normal_range: "",
    abnormal_flag: "",
    ordered_by: "",
    performed_by: "",
  });

  useEffect(() => {
    fetchAdmissions();
    fetchChoices();
  }, []);

  const fetchAdmissions = async () => {
    try {
      setLoadingAdmissions(true);
      setErrorMsg("");

      const res = await axios.get(ADMISSION_API);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setAdmissions(data);
    } catch (error) {
      console.error("Admissions fetch error:", error);
      setErrorMsg("Unable to load admissions.");
    } finally {
      setLoadingAdmissions(false);
    }
  };

  const fetchChoices = async () => {
    try {
      setLoadingChoices(true);

      const res = await axios.get(INVESTIGATION_CHOICES_API);
      const data = res.data || {};

      setCategoryOptions(Array.isArray(data.category_choices) ? data.category_choices : []);
      setStatusOptions(Array.isArray(data.status_choices) ? data.status_choices : []);
      setAbnormalFlagOptions(
        Array.isArray(data.abnormal_flag_choices) ? data.abnormal_flag_choices : []
      );
    } catch (error) {
      console.error("Choices fetch error:", error);
      setCategoryOptions([
        { value: "RADIOLOGY", label: "Radiology" },
        { value: "BIOCHEMISTRY", label: "Biochemistry" },
        { value: "PATHOLOGY", label: "Pathology" },
        { value: "MICROBIOLOGY", label: "Microbiology" },
        { value: "OTHERS", label: "Others" },
      ]);
      setStatusOptions([
        { value: "ORDERED", label: "Ordered" },
        { value: "PENDING", label: "Pending" },
        { value: "COMPLETED", label: "Completed" },
      ]);
      setAbnormalFlagOptions([
        { value: "NORMAL", label: "Normal" },
        { value: "ABNORMAL", label: "Abnormal" },
        { value: "CRITICAL", label: "Critical" },
      ]);
    } finally {
      setLoadingChoices(false);
    }
  };

  const selectedAdmission = useMemo(() => {
    return admissions.find(
      (item) => String(item.admission_id || item.id) === String(form.admission)
    );
  }, [admissions, form.admission]);

  useEffect(() => {
    if (!selectedAdmission) {
      setForm((prev) => ({
        ...prev,
        admission_number: "",
        patient_uhid: "",
        patient_name: "",
        doctor_name: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      admission_number:
        selectedAdmission.admission_number ||
        selectedAdmission.admission_no ||
        selectedAdmission.ip_number ||
        selectedAdmission.ip_no ||
        "",
      patient_uhid:
        selectedAdmission.patient_uhid ||
        selectedAdmission.uhid ||
        "",
      patient_name:
        selectedAdmission.patient_name ||
        selectedAdmission.name ||
        selectedAdmission.full_name ||
        "",
      doctor_name:
        selectedAdmission.doctor_name ||
        selectedAdmission.treating_doctor ||
        selectedAdmission.consultant_name ||
        selectedAdmission.consultant ||
        "",
    }));
  }, [selectedAdmission]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrorMsg("");
    setSuccessMsg("");
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      admission: "",
      admission_number: "",
      patient_uhid: "",
      patient_name: "",
      doctor_name: "",
      category: "",
      test_name: "",
      body_part_sample: "",
      result: "",
      impression_diagnosis: "",
      status: "",
      date: "",
      remarks: "",
      unit: "",
      normal_range: "",
      abnormal_flag: "",
      ordered_by: "",
      performed_by: "",
    });
  };

  const validateForm = () => {
    if (!form.admission) return "Please select admission.";
    if (!form.category) return "Please select category.";
    if (!form.test_name.trim()) return "Please enter test name.";
    if (!form.status) return "Please select status.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");
      setSuccessMsg("");

      const payload = {
        admission: form.admission,
        category: form.category,
        test_name: form.test_name.trim(),
        body_part_sample: form.body_part_sample.trim(),
        result: form.result.trim(),
        impression_diagnosis: form.impression_diagnosis.trim(),
        status: form.status,
        date: form.date || null,
        remarks: form.remarks.trim(),
        unit: form.unit.trim(),
        normal_range: form.normal_range.trim(),
        abnormal_flag: form.abnormal_flag,
        ordered_by: form.ordered_by.trim(),
        performed_by: form.performed_by.trim(),
      };

      await axios.post(INVESTIGATION_API, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setSuccessMsg("Investigation saved successfully.");
      resetForm();
    } catch (error) {
      console.error("Investigation save error:", error);

      if (error.response?.data) {
        setErrorMsg(
          typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
        );
      } else {
        setErrorMsg("Failed to save investigation.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: Colors.bg.app, padding: "32px 18px" }}>
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          background: Colors.bg.paper,
          borderRadius: "24px",
          boxShadow: Colors.shadow.brand,
          overflow: "hidden",
          border: `1px solid ${Colors.border.light}`,
        }}
      >
        <div
          style={{
            padding: "26px 30px 18px",
            borderBottom: `1px solid ${Colors.border.light}`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: "28px", fontWeight: 800, color: Colors.text.title }}>
            Add Investigation
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
              }}
            >
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#f0fff4",
                border: `1px solid ${Colors.status.success}`,
                color: Colors.status.success,
                borderRadius: "12px",
              }}
            >
              {successMsg}
            </div>
          )}

          <div style={{ ...sectionCardStyle, marginBottom: "22px" }}>
            <h3 style={{ marginTop: 0, color: Colors.text.header }}>Admission Selection</h3>
            <label style={labelStyle}>Admission</label>
            <select
              name="admission"
              value={form.admission}
              onChange={handleChange}
              style={inputStyle}
              disabled={loadingAdmissions}
            >
              <option value="">
                {loadingAdmissions ? "Loading admissions..." : "Select admission"}
              </option>
              {admissions.map((item) => {
                const admissionId = item.admission_id || item.id;
                const admissionNumber =
                  item.admission_number || item.admission_no || item.ip_number || item.ip_no || "";
                const patientName = item.patient_name || item.name || item.full_name || "";
                return (
                  <option key={admissionId} value={admissionId}>
                    {admissionNumber} - {patientName}
                  </option>
                );
              })}
            </select>
          </div>

          <div style={{ ...sectionCardStyle, marginBottom: "22px" }}>
            <h3 style={{ marginTop: 0, color: Colors.text.header }}>Patient Details</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle}>Admission Number</label>
                <input type="text" value={form.admission_number} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Patient UHID</label>
                <input type="text" value={form.patient_uhid} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Patient Name</label>
                <input type="text" value={form.patient_name} readOnly style={readOnlyStyle} />
              </div>
              <div>
                <label style={labelStyle}>Doctor Name</label>
                <input type="text" value={form.doctor_name} readOnly style={readOnlyStyle} />
              </div>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <h3 style={{ marginTop: 0, color: Colors.text.header }}>Investigation Details</h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle}>Category</label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select category</option>
                  {categoryOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Test Name</label>
                <input
                  type="text"
                  name="test_name"
                  value={form.test_name}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Body Part / Sample</label>
                <input
                  type="text"
                  name="body_part_sample"
                  value={form.body_part_sample}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select status</option>
                  {statusOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Date</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Unit</label>
                <input
                  type="text"
                  name="unit"
                  value={form.unit}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Normal Range</label>
                <input
                  type="text"
                  name="normal_range"
                  value={form.normal_range}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Abnormal Flag</label>
                <select
                  name="abnormal_flag"
                  value={form.abnormal_flag}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select flag</option>
                  {abnormalFlagOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Ordered By</label>
                <input
                  type="text"
                  name="ordered_by"
                  value={form.ordered_by}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Performed By</label>
                <input
                  type="text"
                  name="performed_by"
                  value={form.performed_by}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Result</label>
                <textarea
                  name="result"
                  value={form.result}
                  onChange={handleChange}
                  style={textareaStyle}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Impression / Diagnosis</label>
                <textarea
                  name="impression_diagnosis"
                  value={form.impression_diagnosis}
                  onChange={handleChange}
                  style={textareaStyle}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Remarks</label>
                <textarea
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <button
              type="button"
              onClick={resetForm}
              style={{
                padding: "12px 18px",
                borderRadius: "12px",
                border: `1px solid ${Colors.border.input}`,
                background: Colors.bg.paper,
                color: Colors.text.body,
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Reset
            </button>

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "12px 22px",
                borderRadius: "12px",
                border: "none",
                background: saving ? Colors.text.sub : Colors.brand.primary,
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 800,
                minWidth: "190px",
              }}
            >
              {saving ? "Saving..." : "Save Investigation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}